import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePresence } from "../../../store/PresenceContext";

import "./index.scss";
import DropdownMenuFloat from "../DropdownMenuFloat";
import classNames from "../../../utils/classNames";
import { formatMessagePreview } from "../../../utils/messageUtils";
import formatConversationTimestamp from "../../../utils/conversationTimestamp";
import UserCardAvatar from "../UserCardAvatar";
import {
    isGroupChatConversation,
    resolveChatAvatarAlt,
    resolveChatAvatarProps,
} from "../../../utils/chatAvatar";

const ChatItem = ({
    chat,
    isSelected,
    isPinned,
    isMuted,
    isTyping,
    onSelect,
    onTogglePin,
    onToggleMute,
    onRequestDelete,
    showDropdown,
    dropdownCoords,
    onRightClick,
    onDropdownClose,
}) => {
    const { t } = useTranslation();
    const { getStatus } = usePresence();
    const isGroupChat = isGroupChatConversation(chat);
    const avatarProps = resolveChatAvatarProps(chat);
    const presence = isGroupChat ? null : getStatus(chat?.avatarUserId);
    const fallbackStatus = typeof chat?.presenceStatus === "string"
        ? chat.presenceStatus.toLowerCase()
        : null;
    const isOnline = presence?.online
        ?? (fallbackStatus ? fallbackStatus === "online" : Boolean(chat?.online));
    const lastSeen = isOnline ? null : (presence?.lastSeen ?? chat?.lastSeen ?? null);

    const unreadCount = chat.unreadMessages || 0;

    const threadTimestamp = useMemo(() => {
        const label = formatConversationTimestamp(chat.lastMessageTimestamp);
        return label || "New";
    }, [chat.lastMessageTimestamp]);

    const messagePreview = useMemo(
        () => formatMessagePreview(chat.latestMessage),
        [chat.latestMessage]
    );
    const avatarStatus = isGroupChat ? undefined : {
        online: isOnline,
        lastSeen,
        placement: 'br',
        size: '12px',
        offset: '2px',
    };

    // Long-press fallback for the context menu: iOS Safari never fires
    // `contextmenu` on touch, so pin/mute/delete would be unreachable there.
    const longPressTimerRef = useRef(null);
    const longPressStartRef = useRef(null);
    const longPressFiredRef = useRef(false);

    const cancelLongPress = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressStartRef.current = null;
    }, []);

    useEffect(() => cancelLongPress, [cancelLongPress]);

    const handleTouchStart = useCallback((event) => {
        if (event.touches.length !== 1) {
            cancelLongPress();
            return;
        }
        const touch = event.touches[0];
        const point = { x: touch.clientX, y: touch.clientY };
        longPressFiredRef.current = false;
        longPressStartRef.current = point;
        longPressTimerRef.current = setTimeout(() => {
            longPressTimerRef.current = null;
            longPressFiredRef.current = true;
            onRightClick?.({
                preventDefault: () => {},
                clientX: point.x,
                clientY: point.y,
            });
        }, 450);
    }, [cancelLongPress, onRightClick]);

    const handleTouchMove = useCallback((event) => {
        const start = longPressStartRef.current;
        const touch = event.touches[0];
        if (!start || !touch) {
            return;
        }
        if (Math.abs(touch.clientX - start.x) > 10 || Math.abs(touch.clientY - start.y) > 10) {
            cancelLongPress();
        }
    }, [cancelLongPress]);

    const handleTouchEnd = useCallback((event) => {
        cancelLongPress();
        if (longPressFiredRef.current) {
            // Swallow the synthetic click so lifting the finger neither
            // selects the chat nor taps a menu item under the touch point.
            event.preventDefault();
            longPressFiredRef.current = false;
        }
    }, [cancelLongPress]);

    const handleContextMenu = useCallback((event) => {
        // Native long-press contextmenu (Android / desktop right click):
        // drop any pending timer so the menu is not opened twice.
        cancelLongPress();
        longPressFiredRef.current = false;
        onRightClick?.(event);
    }, [cancelLongPress, onRightClick]);

    const dropdownItems = useMemo(() => [
        {
            label: isPinned ? "Unpin conversation" : "Pin conversation",
            onClick: () => onTogglePin?.(chat.id),
            icon: (
                <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path
                        fill="currentColor"
                        d="M13.66 2.34a2.34 2.34 0 0 1 0 3.31l-.75.75 2.62 2.62a1.4 1.4 0 0 1 0 1.98l-3.31 3.31a1.4 1.4 0 0 1-1.98 0L7.62 11l-.75.75a2.34 2.34 0 1 1-3.31-3.31l7.02-7.02a2.34 2.34 0 0 1 3.31 0z"
                    />
                </svg>
            ),
        },
        {
            label: isMuted ? "Unmute notifications" : "Mute notifications",
            onClick: () => onToggleMute?.(chat.id),
            icon: (
                <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path
                        fill="currentColor"
                        d="M5 7a5 5 0 0 1 10 0v3.382l1.276 1.276A1 1 0 0 1 15.862 13L14 11.138V7a3 3 0 0 0-6 0v4.138L5.138 13A1 1 0 1 1 3.724 11.66L5 10.382z"
                        opacity="0.5"
                    />
                    <path
                        fill="currentColor"
                        d="M2.293 2.293a1 1 0 0 1 1.414 0l14 14a1 1 0 0 1-1.414 1.414L14.414 15H5a1 1 0 0 1-1-1v-2.586l-1.707-1.707a1 1 0 0 1 1.414-1.414L4 10.414V9.414L2.293 7.707a1 1 0 0 1 0-1.414z"
                    />
                </svg>
            ),
        },
        {
            label: t("actions.deleteConversation"),
            onClick: () => onRequestDelete?.(chat.id),
            icon: (
                <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path
                        fill="currentColor"
                        d="M8 2a2 2 0 0 0-2 2H4.5a1 1 0 1 0 0 2H5v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V6h.5a1 1 0 1 0 0-2H14a2 2 0 0 0-2-2zm0 2h4v2H8zm2 4a1 1 0 0 1 1 1v5a1 1 0 1 1-2 0V9a1 1 0 0 1 1-1z"
                    />
                </svg>
            ),
            tone: "danger",
        },
    ], [chat.id, isMuted, isPinned, onRequestDelete, onToggleMute, onTogglePin, t]);

    return (
        <div
            className={classNames(
                "chat-item",
                {
                    "chat-item--active": isSelected,
                    "chat-item--unread": unreadCount > 0,
                    "chat-item--pinned": isPinned,
                    "chat-item--muted": isMuted,
                }
            )}
            onClick={onSelect}
            onContextMenu={handleContextMenu}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={cancelLongPress}
            role="listitem"
        >
            <span className="chat-item__glow" aria-hidden="true" />
            <UserCardAvatar
                {...avatarProps}
                username={chat.username}
                alt={resolveChatAvatarAlt(chat, "User")}
                className="chat-item__avatar"
                avatarClassName="chat-item__avatar-media"
                radius="50%"
                status={avatarStatus}
            >
                {isPinned && (
                    <span className="chat-item__pin" aria-hidden="true">
                        <svg viewBox="0 0 16 16">
                            <path
                                fill="currentColor"
                                d="M11.6 1.4a2 2 0 0 1 0 2.828l-.586.586 2.122 2.122a1 1 0 0 1 0 1.414l-2.828 2.828a1 1 0 0 1-1.414 0L6.772 8.828l-.586.586a2 2 0 1 1-2.828-2.828l5.657-5.657a2 2 0 0 1 2.828 0z"
                            />
                        </svg>
                    </span>
                )}
            </UserCardAvatar>

            <div className="chat-item__body">
                <div className="chat-item__header">
                    <p className="chat-item__name">{chat.username?.trim() || "Unknown User"}</p>
                    <span className="chat-item__time">{threadTimestamp}</span>
                </div>
                <div className="chat-item__meta">
                    <p className="chat-item__preview" data-typing={isTyping || undefined}>
                        {isTyping ? (
                            <span className="chat-item__typing-indicator">
                                <span className="chat-item__typing-dot" />
                                <span className="chat-item__typing-dot" />
                                <span className="chat-item__typing-dot" />
                            </span>
                        ) : (
                            messagePreview
                        )}
                    </p>
                    <div className="chat-item__tags">
                        {isMuted && <span className="chat-item__badge chat-item__badge--muted">Muted</span>}
                        {isPinned && <span className="chat-item__badge">Pinned</span>}
                    </div>
                </div>
            </div>

            {unreadCount > 0 && (
                <span className="chat-item__unread" aria-label={`${unreadCount} unread messages`}>
                    {unreadCount > 99 ? "99+" : unreadCount}
                </span>
            )}

            {showDropdown && (
                <DropdownMenuFloat
                    position={dropdownCoords}
                    items={dropdownItems}
                    onClose={onDropdownClose}
                />
            )}
        </div>
    );
};

export default ChatItem;
